// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { formatToMoney } from '@shared/ui-kit/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useConfirmPopup, type KpiBoxesProps } from '@shared/ui-kit/components';
import { useSelector } from 'react-redux';
import {
  selectAccountCurrency,
  selectUserId,
} from '@shared/data-access/slices/user';
import { useTranslation } from 'react-i18next';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import { type LoaderStatus } from '../../components/loader';
import {
  selectClientId,
  selectClientInstitutions,
  selectClientInstitutionsIds,
  selectAllPendingInstitutions,
  selectClientHasNuvaraInversisAccount,
} from '@shared/data-access/slices/clients/clients-selectors';
import { useInstitutionSyncData } from '../../providers/institution-sync-provider';
import {
  AdvisoryBankProductType,
  type AdvisoryBank,
  type AdvisoryBankProduct,
} from '@shared/data-access/types/brokerage-types';
import {
  useTagManagementData,
  useObjectTagManagement,
} from '@shared/tag-management';
import { TagObjectType } from '@shared/data-access/interfaces/redux/tags-management-state-types';
import {
  buildAdvisoryBanks,
  filterBanksByTag,
  getAccountsTotals,
} from '../../utils';
import { appSelect, isAdvisorApp } from '@shared/ui-kit/utils/app-utils';
import { isDefined } from '@shared/utils';
import { useSnackbar, type OptionsObject } from 'notistack';
import {
  MODAL_TEMPLATES_OPTIONS,
  showModal,
} from '@shared/ui-kit/components/popups/show-modal';

const commonSnackProps: OptionsObject = {
  variant: 'success',
  anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
};

export type BankAccountUnsharePayload = {
  institutionId: string;
  institutionName: string;
  iban: string;
  name?: string;
};

export const useBankAccounts = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [selectedAccount, setSelectedAccount] = useState<{
    institution: AdvisoryBank;
    product: AdvisoryBankProduct;
  } | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);

  const activeClientId = useSelector(selectClientId);

  const syncClientId = appSelect({
    advisor: activeClientId ?? undefined,
    client: undefined,
  });

  const institutionIds = useSelector(selectClientInstitutionsIds);

  const {
    startSync,
    syncState: { isSyncing },
  } = useInstitutionSyncData();

  const { tags, tagsByObject } = useTagManagementData();

  const showConfirm = useConfirmPopup();
  const { enqueueSnackbar } = useSnackbar();

  const selectedCurrency = useSelector(selectAccountCurrency);
  const userId = useSelector(selectUserId);

  const hasNuvaraInversisAccount = useSelector(
    selectClientHasNuvaraInversisAccount,
  );

  const shouldRefresh = useMemo(() => {
    return institutionIds.length === 0;
  }, [institutionIds.length]);

  const [removeInstitution] = advisoryApi.useDeleteAdvisoryInstitutionsMutation({
    fixedCacheKey: 'removeInstitution',
  });

  const institutionsQueryArgs = useMemo(
    () => ({
      currency: selectedCurrency,
      ...(isDefined(syncClientId) ? { client_id: syncClientId } : {}),
    }),
    [selectedCurrency, syncClientId],
  );

  const institutionsQuery = advisoryApi.useGetAdvisoryInstitutionsQuery(
    institutionsQueryArgs,
    {
      skip: isAdvisorApp && !isDefined(syncClientId),
    },
  );

  const institutions = useSelector(selectClientInstitutions);

  const pendingInstitutions = useSelector(selectAllPendingInstitutions);

  const tagsOptions = useMemo(
    () =>
      [
        { label: t('aggregator.all-accounts', { ns: 'aggregator' }), value: '' },
      ].concat(tags.map(t => ({ label: t.name, value: t.tag_id.toString() }))),
    [tags, t],
  );

  const [selectedTag, setSelectedTag] = useState<string>();

  const banks = useMemo<AdvisoryBank[]>(() => {
    const banksWithBalances = buildAdvisoryBanks(institutions, pendingInstitutions);

    return filterBanksByTag({
      banks: banksWithBalances,
      selectedTag,
      tagsByObject,
    });
  }, [institutions, pendingInstitutions, selectedTag, tagsByObject]);

  const accounts = useMemo(() => banks.flatMap(inst => inst.products), [banks]);

  const accountsCount = useMemo(() => accounts.length, [accounts]);

  const banksCount = useMemo(() => banks.length, [banks]);

  const { investment, liquidity, debt, cash } = useMemo(
    () => getAccountsTotals(accounts),
    [accounts],
  );

  const kpis = useMemo<KpiBoxesProps[]>(
    () => [
      {
        text: t('common.cash', { ns: 'common' }),
        value: formatToMoney(cash, selectedCurrency),
        dataTestid: 'kpi-cash',
        onClick: () => navigate('/aggregation/cashflow'),
        icon: 'Wallet',
        color: 'main',
      },
      {
        text: t('glossary.investment', { ns: 'glossary' }),
        value: formatToMoney(investment, selectedCurrency),
        dataTestid: 'kpi-investment',
        onClick: () => navigate('/aggregation/investment'),
        icon: 'TrendingUp',
        color: 'success',
      },
      {
        text: t('common.current-liquidity', { ns: 'common' }),
        value: formatToMoney(liquidity, selectedCurrency),
        dataTestid: 'kpi-available-liquidity',
        onClick: () => navigate('/aggregation/cashflow'),
        icon: 'DollarSign',
        color: 'secondary',
      },
      {
        text: t('glossary.debt', { ns: 'glossary' }),
        value: formatToMoney(debt, selectedCurrency),
        dataTestid: 'kpi-debt',
        onClick: () => navigate('/aggregation/loans'),
        icon: 'CreditCard',
        color: 'danger',
      },
    ],
    [t, cash, selectedCurrency, investment, liquidity, debt, navigate],
  );

  const status = useMemo<LoaderStatus>(() => {
    const isLoading = [institutionsQuery.isLoading].some(Boolean);

    const isFetching = [institutionsQuery.isFetching].some(Boolean);

    return isLoading
      ? 'pending'
      : isFetching
      ? 'fetching'
      : isLoading
      ? 'pending'
      : 'fulfilled';
  }, [institutionsQuery.isFetching, institutionsQuery.isLoading]);

  const onRefresh = useCallback(
    (institutionId: string, institutionName: string) => {
      if (shouldRefresh) {
        institutionsQuery.refetch();

        return;
      }

      startSync(
        {
          ...(isDefined(syncClientId) ? { client_id: syncClientId } : {}),
          connection_ids:
            institutionId && institutionId.toLowerCase() !== 'all'
              ? [institutionId]
              : institutionIds,
        },
        institutionId,
        institutionName,
      );
    },
    [institutionIds, institutionsQuery, shouldRefresh, startSync, syncClientId],
  );

  const onRemoveInstitution = useCallback(
    (institutionId: string, institutionName: string) => {
      if (isAdvisorApp) {
        return;
      }

      showConfirm({
        title: t('accounts.remove-institution', { ns: 'components' }),
        message: t('accounts.remove-institution-message', {
          ns: 'components',
          institutionName,
        }),
        confirmText: t('common.remove', { ns: 'common' }),
        onConfirm: () => {
          removeInstitution({ institution_id: institutionId }).unwrap();
        },
      });
    },
    [showConfirm, t, removeInstitution],
  );

  const [unshareAdvisoryUsersAccountsMutation] =
    advisoryApi.useUnshareAdvisoryUsersAccountsMutation();

  const onUnshareAccount = useCallback(
    (sharedAccount: BankAccountUnsharePayload) => {
      if (!isAdvisorApp) {
        return;
      }

      if (
        !isDefined(userId) ||
        !isDefined(syncClientId) ||
        !sharedAccount.iban ||
        !sharedAccount.institutionId
      ) {
        showModal({
          ...MODAL_TEMPLATES_OPTIONS(t).UNHANDLED_ERROR,
          title: t('accounts.error-removing-institution', {
            ns: 'components',
          }),
          content: t('accounts.error-request', { ns: 'components' }),
          buttons: {
            confirm: { show: true, text: t('common.ok', { ns: 'common' }) },
          },
        });

        return;
      }

      const institutionName =
        sharedAccount.institutionName || sharedAccount.name || '';

      showConfirm({
        title: t('accounts.remove-institution', { ns: 'components' }),
        message: t('accounts.remove-institution-message', {
          ns: 'components',
          institutionName,
        }),
        confirmText: t('common.confirm', { ns: 'common' }),
        onConfirm: async () => {
          try {
            await unshareAdvisoryUsersAccountsMutation({
              client_id: syncClientId,
              user_id: userId,
              institution_id: sharedAccount.institutionId,
              ibans: [sharedAccount.iban],
            }).unwrap();

            enqueueSnackbar(
              t('accounts.institution-no-longer-shared', { ns: 'components' }),
              {
                ...commonSnackProps,
              },
            );
          } catch (error) {
            console.error('Error in unshareAdvisoryUsersAccounts:', error);
            showModal({
              ...MODAL_TEMPLATES_OPTIONS(t).UNHANDLED_ERROR,
              title: t('accounts.error-removing-institution', {
                ns: 'components',
              }),
              content: t('accounts.error-request', { ns: 'components' }),
              buttons: {
                confirm: { show: true, text: t('common.ok', { ns: 'common' }) },
              },
            });
          }
        },
      });
    },
    [
      showConfirm,
      t,
      userId,
      syncClientId,
      unshareAdvisoryUsersAccountsMutation,
      enqueueSnackbar,
    ],
  );

  const openDrawerForAccount = useCallback(
    (id: string, iban: string) => {
      const bank = banks.find(institution => institution.id === id);

      if (!bank) return;

      const product = bank.products
        .toSorted((a, b) => {
          const order: Record<AdvisoryBankProductType, number> = {
            [AdvisoryBankProductType.Portfolio]: 0,
            [AdvisoryBankProductType.Loan]: 1,
            [AdvisoryBankProductType.Card]: 2,
            [AdvisoryBankProductType.Account]: 3,
          };
          const aType = order[a.product_type];
          const bType = order[b.product_type];

          if (aType !== bType) return aType - bType;

          return a.iban.localeCompare(b.iban);
        })
        .find(account => account.iban === iban);

      if (!product) return;

      setSelectedAccount({ institution: bank, product });

      setDrawerOpen(true);
    },
    [banks],
  );

  const onDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setSelectedAccount(null);
  }, []);

  const objectTagManagement = useObjectTagManagement({
    objectType: TagObjectType.Account,
    objectId: selectedAccount?.product?.iban ?? '_',
    onClose: onDrawerClose,
  });

  const onDrawerOpen = useCallback(() => setDrawerOpen(true), []);

  useEffect(() => {
    if (pendingInstitutions.length > 0) {
      startSync(
        {
          ...(isDefined(syncClientId) ? { client_id: syncClientId } : {}),
          connection_ids: pendingInstitutions.map(institution => institution.id),
        },
        'all',
        'all',
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingInstitutions.length]);

  return {
    t,
    dataForTable: banks,
    kpis,
    onRemoveInstitution: isAdvisorApp ? undefined : onRemoveInstitution,
    onUnshareAccount: isAdvisorApp ? onUnshareAccount : undefined,
    onRefresh,
    status,
    institutions,
    isSyncing,
    selectedAccount,
    openDrawerForAccount,
    drawerOpen,
    onDrawerClose: objectTagManagement.onCloseWithSync,
    onDrawerOpen,
    tags: objectTagManagement.tags,
    initialAssignedTags: objectTagManagement.initialAssignedTags,
    onChangeTagIds: objectTagManagement.onChangeTagIds,
    onCreateTag: objectTagManagement.onCreateTag,
    banks,
    accountsCount,
    banksCount,
    tagsOptions,
    selectedTag,
    setSelectedTag,
    hasNuvaraInversisAccount,
  };
};
